import { AlfredFiatAccountType } from "src/api";

/**
 * Per-rail validation rules for Alfred fiatAccount registration.
 *
 * Source of truth: https://alfredpay.readme.io/docs/registering-payment-methods
 * Plus 2026-05 live probes against Alfred's dev sandbox for fields the
 * docs miss (`phoneNumber`/`pep`/`cuit` for AR; `phoneNumber`/`typeDocumentCol`
 * for CO). Keep this table in sync with the BE constraint in
 * vakotrade `dto/alfred-fiat-account-fields.validator.ts` — they share
 * intent (Alfred is the only authority, but a local mirror catches
 * obvious mistakes before a round-trip and gives field-level UX).
 */
export interface RuleDef {
  /** Path inside `fiatAccountFields` */
  key: string;
  label: string;
  required: boolean;
  placeholder?: string;
  /** Validation predicates run on submit and onBlur. Return error string or null. */
  validate?: (value: string) => string | null;
  /** Type hint for the input component (default: text). */
  inputType?: "text" | "checkbox";
}

const digits = (n: number) => (v: string) =>
  new RegExp(`^\\d{${n}}$`).test(v) ? null : `Must be exactly ${n} digits`;

const digitsRange = (min: number, max: number) => (v: string) =>
  new RegExp(`^\\d{${min},${max}}$`).test(v) ? null : `Must be ${min}–${max} digits`;

const oneOf = (allowed: string[]) => (v: string) =>
  allowed.includes(v.toUpperCase()) ? null : `Must be one of: ${allowed.join(", ")}`;

const aliasPattern = (v: string) =>
  /^[a-zA-Z0-9.\-]{6,20}$/.test(v)
    ? null
    : "6–20 chars, letters/digits/dot/dash only";

export const RAIL_RULES: Record<AlfredFiatAccountType, RuleDef[]> = {
  PIX: [
    { key: "accountNumber", label: "CPF (PIX key)", required: true, placeholder: "11 digits", validate: digits(11) },
    { key: "accountType", label: "Account type", required: true, placeholder: "CPF", validate: oneOf(["CPF"]) },
    { key: "accountName", label: "Account holder", required: false },
    { key: "accountBankCode", label: "Bank code", required: false, placeholder: "e.g. 341" },
  ],
  SPEI: [
    { key: "accountNumber", label: "CLABE", required: true, placeholder: "18 digits", validate: digits(18) },
    { key: "accountType", label: "Account type", required: true, placeholder: "CLABE", validate: oneOf(["CLABE"]) },
    { key: "accountName", label: "Account holder", required: false },
  ],
  COELSA: [
    // NB: `cuit/phoneNumber/pep` belong to the KYC submission, not the
    // fiatAccount payload — Alfred returns 111302 "Invalid parameter(s)
    // cuit, phoneNumber, pep" if we send them here. Alfred autopopulates
    // bankName/holderName/bankCode/cuit on the response from accountNumber.
    { key: "accountNumber", label: "CBU", required: true, placeholder: "22 digits", validate: digits(22) },
    { key: "accountType", label: "Account type", required: true, placeholder: "CVU or Alias", validate: oneOf(["CVU", "ALIAS"]) },
    { key: "accountAlias", label: "Alias", required: false, placeholder: "alphanum/dash/dot, 6–20", validate: (v) => (v ? aliasPattern(v) : null) },
  ],
  ACH: [
    // Colombia ACH. Alfred requires `accountName` (the bank name, e.g.
    // "Bancamia") — the doc shows it as a hint but the API treats it as
    // required. `typeDocumentCol/phoneNumber` are KYC-only and rejected
    // here (Alfred 111302).
    { key: "accountNumber", label: "Account number", required: true, placeholder: "10–11 digits", validate: digitsRange(10, 11) },
    { key: "accountType", label: "Account type", required: true, placeholder: "AHORRO or NEQUI", validate: oneOf(["AHORRO", "NEQUI"]) },
    { key: "accountName", label: "Bank name", required: true, placeholder: "e.g. Bancamia" },
  ],
  ACH_DOM: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true },
    { key: "accountName", label: "Account holder", required: false },
  ],
  BANK_USA: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true, placeholder: "checking / savings", validate: oneOf(["CHECKING", "SAVINGS"]) },
    { key: "routingNumber", label: "Routing number (ABA)", required: true, placeholder: "9 digits", validate: digits(9) },
    { key: "bankStreet", label: "Bank street", required: true },
    { key: "bankCity", label: "Bank city", required: true },
    { key: "bankState", label: "Bank state", required: true },
    { key: "bankPostalCode", label: "Bank ZIP", required: true },
  ],
  BANK_CN: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true },
    { key: "networkIdentifier", label: "SWIFT", required: true },
    { key: "accountName", label: "Account holder", required: true },
    { key: "bankStreet", label: "Bank street", required: true },
    { key: "bankCity", label: "Bank city", required: true },
    { key: "bankState", label: "Bank state", required: true },
    { key: "bankPostalCode", label: "Bank postal code", required: true },
  ],
  ACH_CHL: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true },
  ],
  ACH_BOL: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true },
  ],
  B89: [
    { key: "accountNumber", label: "Account number", required: true },
    { key: "accountType", label: "Account type", required: true },
  ],
};

/** Per-field error map, used by both the form and submit gating. */
export function validateFiatAccountFields(
  type: AlfredFiatAccountType,
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const rules = RAIL_RULES[type] ?? [];
  for (const r of rules) {
    const raw = values[r.key];
    const isCheckbox = r.inputType === "checkbox";
    const v = isCheckbox ? (raw === true ? "true" : raw === false ? "false" : "") : (typeof raw === "string" ? raw : "");
    if (!v) {
      if (r.required) errors[r.key] = "Required";
      continue;
    }
    if (r.validate && !isCheckbox) {
      const msg = r.validate(v);
      if (msg) errors[r.key] = msg;
    }
  }
  return errors;
}
